import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { translate, TranslationKeys } from '../../../i18n';
import { GameDto } from '../../../models/game/dto/game.dto';
import {
    GameCancellationPolicy,
    GameCancellationReason,
} from '../../../models/payment/dto/game-cancellation.dto';
import { GAME_QUERY_KEY } from './useGetGame';

export interface CancelGameInput {
    gameId: number;
    policy: GameCancellationPolicy;
    reason?: GameCancellationReason;
    reasonText?: string;
}

interface CancelGameBody {
    policy: GameCancellationPolicy;
    reason?: GameCancellationReason;
    reasonText?: string;
}

const SUCCESS_KEY_BY_POLICY: Record<
    GameCancellationPolicy,
    { title: TranslationKeys; description: TranslationKeys }
> = {
    [GameCancellationPolicy.REFUND]: {
        title: 'game.canceled_successfully' as TranslationKeys,
        description: 'game.game_has_been_canceled_refunds_processed' as TranslationKeys,
    },
    [GameCancellationPolicy.NO_REFUND]: {
        title: 'game.canceled_successfully' as TranslationKeys,
        description: 'game.game_canceled_no_refund_description' as TranslationKeys,
    },
    [GameCancellationPolicy.WALLET_CREDIT]: {
        title: 'game.canceled_successfully' as TranslationKeys,
        description: 'game.game_canceled_wallet_credit_description' as TranslationKeys,
    },
};

export const useCancelGame = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ gameId, policy, reason, reasonText }: CancelGameInput) => {
            return apiClient.fetchApi<GameDto, CancelGameBody>({
                endpoint: `payments/cancel-game/${gameId}`,
                method: 'POST',
                body: {
                    policy,
                    reason,
                    reasonText,
                },
            });
        },
        onSuccess: (_data, { gameId, policy }) => {
            const copy = SUCCESS_KEY_BY_POLICY[policy];
            toast({
                title: translate(copy.title),
                description: translate(copy.description),
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            queryClient.invalidateQueries({ queryKey: GAME_QUERY_KEY(gameId) });
        },
        onError: (error: any) => {
            toast({
                title: translate('game.error_canceling_game'),
                description: error?.message || translate('common.unexpected_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });
};
